import React, { useState, useEffect, createRef } from "react";
import {
  Table2,
  Column,
  Cell,
  ColumnHeaderCell,
  RenderMode,
} from "@blueprintjs/table";
import { mefMontantStr } from "../../../utils/mef";
import { authenticatedGetFetch } from "../../../utils/AuthenticatedFetch";
import { HTMLSelect, Button, Tag, Spinner } from "@blueprintjs/core";

function clamp(val, min, max) {
  if (val == null) return val;
  else return Math.min(Math.max(val, min), max);
}

function resizeColumns(ColumnsLength, table) {
  const minColumnWidth = 10;
  const maxColumnWidth = 700;
  let newColumnWidths = [];

  if (table.current) {
    for (let i = 0; i < ColumnsLength; i++) {
      const width = table.current.locator.getWidestVisibleCellInColumn(i);
      const clampedWidth = clamp(width, minColumnWidth, maxColumnWidth) + 30;
      newColumnWidths.push(clampedWidth);
    }

    newColumnWidths[ColumnsLength - 1] += table.current.grid.ghostWidth;

    return newColumnWidths;
  } else return undefined;
}

export function FinancialTable(props) {
  var columns = [];
  for (const column of props.columns) {
    columns.push(
      <Column
        key={props.title + column.title}
        id={column.title}
        name={column.title}
        cellRenderer={(rowIndex, columnIndex) =>
          column.render ? (
            <Cell>
              {column.render(
                props.data[rowIndex][props.columns[columnIndex].field]
              )}
            </Cell>
          ) : (
            <Cell style={{ textAlign: "right" }}>
              {props.data[rowIndex][props.columns[columnIndex].field] !==
              undefined
                ? props.displayCount === false
                  ? mefMontantStr(
                      props.data[rowIndex][props.columns[columnIndex].field].sum
                    )
                  : mefMontantStr(
                      props.data[rowIndex][props.columns[columnIndex].field].sum
                    ) +
                    " (" +
                    props.data[rowIndex][props.columns[columnIndex].field].cnt +
                    ")"
                : null}
            </Cell>
          )
        }
      />
    );
  }
  return (
    <Table2 numRows={props.data.length} enableRowHeader={false}>
      {columns}
    </Table2>
  );
}

export function TableCustom(props) {
  const [Data, setData] = useState(undefined);
  const [TotalCount, setTotalCount] = useState(undefined);
  const [PageSize, setPageSize] = useState(20);
  const [CurrentPage, setCurrentPage] = useState(0);
  const [ColomnWidth, setColomnWidth] = useState(undefined);
  const [OrderDirection, setOrderDirection] = useState(
    props.orderDirection === undefined ? "desc" : props.orderDirection
  );
  const [Orderby, setOrderby] = useState(
    props.orderBy === undefined ? undefined : props.orderBy
  );
  const table = createRef();

  useEffect(() => {
    setCurrentPage(0);
  }, [props.url]);

  useEffect(() => {
    var params = {};
    params = {
      pageSize: PageSize,
      page: CurrentPage,
      orderDirection: OrderDirection,
      orderBy: Orderby,
      search: undefined,
    };
    setData([]);
    authenticatedGetFetch(
      props.url +
        (props.url.includes("?") ? "&" : "?") +
        new URLSearchParams(params),
      (data) => {
        setData(data.data);
        setTotalCount(data.totalCount);
      }
    );
  }, [props.url, PageSize, CurrentPage, OrderDirection, Orderby]);

  var columns = [];
  for (var column in props.columns) {
    columns.push(
      <Column
        key={props.columns[column].title}
        id={props.columns[column].title}
        columnHeaderCellRenderer={(column) => (
          <ColumnHeaderCell>
            <div style={{ marginLeft: "5px" }}>
              {props.columns[column].title}
              {props.columns[column].sortable && (
                <Tag
                  icon={"double-caret-vertical"}
                  minimal={true}
                  style={{ marginLeft: "5px" }}
                  onClick={() => {
                    OrderDirection === "asc"
                      ? setOrderDirection("desc")
                      : setOrderDirection("asc");
                    setOrderby(props.columns[column].field);
                  }}
                />
              )}
            </div>
          </ColumnHeaderCell>
        )}
        cellRenderer={(rowIndex, columnIndex) => (
          <Cell
            intent={
              Data[rowIndex] != null &&
              props.columns[columnIndex] != null &&
              props.columns[columnIndex].intent != null
                ? props.columns[columnIndex].intent(Data[rowIndex])
                : null
            }
          >
            {Data[rowIndex] != null &&
            props.columns[columnIndex] != null &&
            props.columns[columnIndex].render != null
              ? props.columns[columnIndex].render(Data[rowIndex], rowIndex)
              : null}
          </Cell>
        )}
      />
    );
  }

  return (
    <div>
      {Data === undefined ? (
        <Spinner />
      ) : (
        <div>
          <Table2
            numRows={Data.length}
            enableRowHeader={false}
            ref={table}
            onCompleteRender={() =>
              setColomnWidth(resizeColumns(props.columns.length, table))
            }
            columnWidths={ColomnWidth}
            renderMode={props.renderMode || RenderMode.BATCH}
          >
            {columns}
          </Table2>

          {/* pagination */}
          {props.paging === true && (
            <HTMLSelect
              options={[
                { value: 20 },
                { value: 30 },
                { value: 50 },
                { value: 100 },
                { value: 200 },
              ]}
              onChange={(s) => {
                setPageSize(s.target.value);
                setCurrentPage(0);
              }}
              value={PageSize}
            />
          )}

          {props.paging === true && (
            <div className="flexbox-center">
              <div>
                {/* first page */}
                <Button
                  minimal={true}
                  icon="double-chevron-left"
                  onClick={() => {
                    if (CurrentPage > 0) setCurrentPage(0);
                  }}
                />
                {/* previous page */}
                <Button
                  minimal={true}
                  icon="chevron-left"
                  onClick={() => {
                    if (CurrentPage > 0) setCurrentPage(CurrentPage - 1);
                  }}
                />
                {/* current data display */}
                {CurrentPage * PageSize + 1} -{" "}
                {Math.min((CurrentPage + 1) * PageSize, TotalCount)} /{" "}
                {TotalCount}
                {/* next page */}
                <Button
                  minimal={true}
                  icon="chevron-right"
                  onClick={() => {
                    const lastPage = Math.ceil(TotalCount / PageSize) - 1;
                    if (CurrentPage < lastPage) setCurrentPage(CurrentPage + 1);
                  }}
                />
                {/* last page */}
                <Button
                  minimal={true}
                  icon="double-chevron-right"
                  onClick={() => {
                    const lastPage = Math.ceil(TotalCount / PageSize) - 1;
                    if (CurrentPage < lastPage) setCurrentPage(lastPage);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
